import Spinner from '@/components/atoms/Spinner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useEmailMarketing } from '@/hooks/useEmailMarketing';
import { emailMarketingStore } from '@/state/EmailMarketingStore';
import { Formik } from 'formik';
import {
  AlertTriangle,
  Check,
  Clock,
  Copy,
  HelpCircle,
  Mail,
  Plus,
  Server,
  X,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';

const setupSchema = Yup.object().shape({
  domain: Yup.string()
    .trim()
    .matches(
      /^((?!-))(xn--)?[a-z0-9-]+(?<!-)\.[a-z]{2,}(\.[a-z]{2,})?$/,
      'Enter a valid domain',
    )
    .required('Domain is required'),
  senderName: Yup.string().trim().optional().default('sparkhub'),
});

const Setup = () => {
  const { disconnectDomain, verifyDomain, checkDomain } = useEmailMarketing();
  const domainDetails = emailMarketingStore?.setupResponse;
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  const copyToClipboard = (text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedStates(prev => ({ ...prev, [key]: true }));
    setTimeout(
      () => setCopiedStates(prev => ({ ...prev, [key]: false })),
      2000,
    );
  };

  const removeDomain = () => {
    disconnectDomain();
  };

  useEffect(() => {
    checkDomain();
  }, []);

  if (emailMarketingStore?.loading && !emailMarketingStore?.showSpinner) {
    return (
      <div className='min-h-[400px] flex justify-center items-center'>
        <Spinner size={30} />
      </div>
    );
  }

  return (
    <div>
      <Card className='p-6 mb-6'>
        <div className='space-y-4'>
          <div className='space-y-2'>
            <h3 className='text-lg font-semibold'>Add New Domain</h3>
            <Formik
              initialValues={{
                domain: '',
                senderName: '',
              }}
              onSubmit={verifyDomain}
              validationSchema={setupSchema}
            >
              {({ values, handleBlur, handleChange, handleSubmit, errors }) => {
                return (
                  <form onSubmit={handleSubmit} id='add_domain'>
                    <div className='flex flex-col space-y-1'>
                      <div className='relative'>
                        <div className='flex items-center'>
                          <label
                            htmlFor='domain-input'
                            className='text-md font-medium mb-1 flex items-center'
                          >
                            Enter your domain
                            <div className='relative ml-1 group'>
                              <HelpCircle className='h-4 w-4 text-gray-400' />
                              <div className='absolute left-full ml-2 hidden group-hover:block text-white text-sm p-2 rounded w-64 z-10 bg-gray-800'>
                                You can find your domain in your website URL,
                                e.g. stickycards.co
                              </div>
                            </div>
                          </label>
                        </div>
                        <div className='flex flex-row gap-2'>
                          <div className='flex-grow'>
                            <Input
                              id='domain-input'
                              placeholder='e.g. example.com'
                              name='domain'
                              onChange={handleChange}
                              onBlur={handleBlur}
                              disabled={!!domainDetails?.domain}
                              value={values?.domain}
                              className={`focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary ${
                                errors.domain ? 'border-red-500' : ''
                              }`}
                            />
                            <p className='text-xs text-gray-500 mt-1'>
                              {` Enter your company's domain — not an email
                              address.`}
                            </p>
                            {errors.domain && (
                              <p className='text-xs text-red-500 mt-1'>
                                {errors.domain}
                              </p>
                            )}
                          </div>
                          <Button
                            type='submit'
                            disabled={
                              !!domainDetails?.domain ||
                              emailMarketingStore?.loading ||
                              !values?.domain
                            }
                            className='whitespace-nowrap bg-[#033eb5] hover:bg-[#022b7e] text-white'
                          >
                            {!domainDetails?.domain &&
                              !emailMarketingStore?.loading && (
                                <>
                                  <Plus className='mr-2 h-4 w-4 text-white' />{' '}
                                  Add Domain
                                </>
                              )}
                            {domainDetails?.domain && 'Domain Added!'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </form>
                );
              }}
            </Formik>
          </div>
        </div>
      </Card>

      {!domainDetails?.domain ? (
        <Card className='p-6 mb-6'>
          <div className='space-y-4'>
            <h3 className='text-lg font-semibold'>Instructions</h3>
            <div className='bg-gray-50 p-4 rounded-lg'>
              <h4 className='font-medium mb-2'>Add Domain</h4>
              <p className='text-md text-gray-600'>
                Add a domain to send emails from. You will need to verify the
                domain by adding the DNS records provided to your domain.
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Card className='p-6'>
            <div className='space-y-2'>
              <div className='flex justify-between items-center'>
                <h3 className='text-lg font-semibold'>Next Steps</h3>
                <Button
                  variant='ghost'
                  size='icon'
                  onClick={e => {
                    e.stopPropagation();
                    removeDomain();
                  }}
                >
                  <X className='h-3 w-3' />
                </Button>
              </div>
              <div className='bg-gray-50 p-4 rounded-lg'>
                {domainDetails?.status === 'pending' && (
                  <Alert>
                    <AlertTriangle className='h-4 w-4' />
                    <AlertTitle className='text-md'>
                      Verify Your Domain
                    </AlertTitle>
                    <AlertDescription className='text-md'>
                      {`Add the following DNS records to your domain's DNS
                      settings to complete verification.`}
                    </AlertDescription>
                  </Alert>
                )}
                {domainDetails?.status === 'success' && (
                  <Alert>
                    <Check className='h-4 w-4' />
                    <AlertTitle className='text-md'>Domain Verified</AlertTitle>
                    <AlertDescription className='text-md'>
                      {domainDetails?.domain} has been successfully verified.
                    </AlertDescription>
                  </Alert>
                )}
                {domainDetails?.status === 'failed' && (
                  <Alert variant='destructive'>
                    <AlertTriangle className='h-4 w-4' />
                    <AlertTitle className='text-md'>
                      Verification Failed
                    </AlertTitle>
                    <AlertDescription className='text-md'>
                      Unable to verify {domainDetails?.domain}. Please check
                      your DNS settings.
                    </AlertDescription>
                  </Alert>
                )}

                <div className='relative overflow-x-auto rounded-lg border  mt-4'>
                  <table className='w-full text-sm text-left'>
                    <thead className='text-xs uppercase bg-gray-50'>
                      <tr>
                        <th className='px-4 py-3'>Type</th>
                        <th className='px-4 py-3'>Name</th>
                        <th className='px-4 py-3'>Priority</th>
                        <th className='px-4 py-3'>Value</th>
                        <th className='px-4 py-3'>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {domainDetails?.domainRecords?.map((record, index) => (
                        <tr
                          key={index}
                          className={`${
                            index === domainDetails?.domainRecords?.length - 1
                              ? 'border-b-0'
                              : 'border-b'
                          }  bg-white`}
                        >
                          <td className='px-4 py-3 !border-0 text-md'>
                            {record.type}
                          </td>
                          <td className='px-4 py-3 !border-0 text-md flex items-center gap-2'>
                            {record.key}
                            <div className='relative'>
                              <Button
                                variant='ghost'
                                size='icon'
                                onClick={() =>
                                  copyToClipboard(record.key, `name-${index}`)
                                }
                              >
                                <Copy className='h-3 w-3' />
                              </Button>
                              {copiedStates[`name-${index}`] && (
                                <span className='absolute left-full ml-2 bg-gray-800 text-white text-xs px-2 py-1 rounded'>
                                  Copied!
                                </span>
                              )}
                            </div>
                          </td>
                          <td className='px-4 py-3 !border-0 text-md '>
                            {record?.priority || '-'}
                          </td>
                          <td className='px-4 py-3 !border-0  font-mono text-md flex items-center gap-2'>
                            {record.value}
                            <div className='relative'>
                              <Button
                                variant='ghost'
                                size='icon'
                                onClick={() =>
                                  copyToClipboard(
                                    record.value,
                                    `value-${index}`,
                                  )
                                }
                              >
                                <Copy className='h-3 w-3' />
                              </Button>
                              {copiedStates[`value-${index}`] && (
                                <span className='absolute left-full ml-2 bg-gray-800 text-white text-xs px-2 py-1 rounded'>
                                  Copied!
                                </span>
                              )}
                            </div>
                          </td>
                          <td className='px-4 py-3 !border-0 '>
                            <div className='relative'>
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() =>
                                  copyToClipboard(
                                    `Type: ${record.type}, Name: ${record.key}, Value: ${record.value}`,
                                    `full-${index}`,
                                  )
                                }
                              >
                                Copy All
                              </Button>
                              {copiedStates[`full-${index}`] && (
                                <span className='absolute right-full mr-2 bg-gray-800 text-white text-xs px-2 py-1 rounded'>
                                  Copied!
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className='mt-4 flex justify-between items-center'>
                  <Button
                    disabled={true}
                    variant='outline'
                    onClick={() => setShowAdminModal(true)}
                    className='text-[#033eb5]'
                  >
                    <Mail className='h-4 w-4 mr-2' />
                    Send to Web Admin
                  </Button>
                  <Button
                    onClick={checkDomain}
                    disabled={
                      emailMarketingStore?.loading ||
                      domainDetails?.status === 'success'
                    }
                    className='bg-[#033eb5] hover:bg-[#022b7e] text-white'
                  >
                    {!emailMarketingStore?.loading && 'Check DNS Status'}
                    {emailMarketingStore?.loading && 'Checking...'}
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          <Card className='p-6 mt-6'>
            <div className='space-y-4'>
              <h3 className='text-lg font-semibold'>Instructions</h3>
              <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                <div className='bg-gray-50 p-4 rounded-lg text-center'>
                  <div className='w-12 h-12 bg-[#033eb5] text-white rounded-full flex items-center justify-center mx-auto mb-3'>
                    1
                  </div>
                  <h4 className='font-medium mb-1'>Enter Domain</h4>
                  <p className='text-xs text-gray-600'>
                    {` Add your company's domain name`}
                  </p>
                </div>
                <div className='bg-gray-50 p-4 rounded-lg text-center'>
                  <div className='w-12 h-12 bg-[#033eb5] text-white rounded-full flex items-center justify-center mx-auto mb-3'>
                    <Server className='h-5 w-5' />
                  </div>
                  <h4 className='font-medium mb-1'>Add DNS Records</h4>
                  <p className='text-xs text-gray-600'>
                    {` Update your domain's DNS settings`}
                  </p>
                </div>
                <div className='bg-gray-50 p-4 rounded-lg text-center'>
                  <div className='w-12 h-12 bg-[#033eb5] text-white rounded-full flex items-center justify-center mx-auto mb-3'>
                    <Clock className='h-5 w-5' />
                  </div>
                  <h4 className='font-medium mb-1'>Wait for Verification</h4>
                  <p className='text-xs text-gray-600'>
                    Can take up to 24 hours
                  </p>
                </div>
                <div className='bg-gray-50 p-4 rounded-lg text-center'>
                  <div className='w-12 h-12 bg-[#033eb5] text-white rounded-full flex items-center justify-center mx-auto mb-3'>
                    <Check className='h-5 w-5' />
                  </div>
                  <h4 className='font-medium mb-1'>Start Sending</h4>
                  <p className='text-xs text-gray-600'>
                    Send branded emails from your domain
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </>
      )}

      {showAdminModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-100'>
          <div className='bg-white p-6 rounded-lg w-full max-w-md'>
            <h3 className='text-lg font-semibold mb-4'>
              Send DNS Instructions to Web Admin
            </h3>
            <div className='space-y-4'>
              <div>
                <label
                  htmlFor='admin-email'
                  className='block text-md font-medium mb-1'
                >
                  Admin Email
                </label>
                <Input
                  id='admin-email'
                  type='email'
                  placeholder='admin@example.com'
                  value={adminEmail}
                  onChange={e => setAdminEmail(e.target.value)}
                  className='focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-ring focus-visible:ring-offset-0 focus-within:border focus-within:border-secondary'
                />
              </div>
              <div className='flex justify-end space-x-2'>
                <Button
                  variant='outline'
                  onClick={() => setShowAdminModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  className='bg-[#033eb5] hover:bg-[#022b7e] text-white'
                  onClick={() => {
                    // TODO: Implement send to admin functionality
                    setShowAdminModal(false);
                  }}
                  disabled={!adminEmail.includes('@')}
                >
                  Send Instructions
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(Setup);
